import { useMemo } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { ButtonGroup } from '@cbhq/cds-web/buttons/ButtonGroup';
import { Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextDisplay3, TextTitle4 } from '@cbhq/cds-web/typography';

import { ReactComponent as CommerceApiKeyImg } from ':cloud/assets/Products/CommerceApiKey.svg';
import { ReactComponent as CommerceHeaderImg } from ':cloud/assets/Products/CommerceHeader.svg';
import { ReactComponent as CommerceUseCaseImg } from ':cloud/assets/Products/CommerceUseCase.svg';
import { ReactComponent as OckTemplateImg } from ':cloud/assets/Products/OckTemplate.svg';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import {
  COMMERCE_CREATE_API_LINK,
  COMMERCE_OCK_TEMPLATE_LINK,
  COMMERCE_PORTAL_LINK,
  ONCHAIN_KIT_CHECKOUT_LINK,
} from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';
import { Layout } from ':cloud/widgets/layout';
import { FULL_PAGE_MAX_WIDTH, MAIN_CONTAINER_MIN_WIDTH } from ':cloud/widgets/product/constants';
import { ExampleSection, ExampleSectionItem } from ':cloud/widgets/sharedcomponents/ExampleSection';
import {
  KeyFeatureItem,
  KeyFeaturesSection,
} from ':cloud/widgets/sharedcomponents/KeyFeaturesSection';

const IMAGE_STYLE = { height: 'auto', width: '510px' };
const MOBILE_IMAGE_STYLE = { height: 'auto', width: '100%', maxWidth: '510px' };
/* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop */
const EXAMPLE_IMAGE_STYLE = { maxWidth: '100%', height: 'auto', width: '100%' };

const keyFeatures: KeyFeatureItem[] = [
  {
    iconName: 'layerNetworks',
    title: 'Comprehensive merchant tooling',
    description:
      'After you’re set up, you’ll get real-time sales tracking and reporting, helping you make data-driven decisions.',
    iconType: 'pictogram',
  },
  {
    iconName: 'browserTransaction',
    title: 'Fast integrations',
    description:
      'Get set up with Commerce in minutes, no backend needed. Our flexible API makes it easy to pass metadata and trigger web hooks.',
    iconType: 'pictogram',
  },
  {
    iconName: 'sendPaymentToOthers',
    title: 'Seamless customer experience',
    description:
      'Your customers get 1-click payments, zero fees, instant settlement, and flexible funding options.',
    iconType: 'pictogram',
  },
];

const learnItems: ExampleSectionItem[] = [
  {
    title: 'Create your API key',
    description: 'Learn how to start accepting crypto payments with Commerce API.',
    cta: 'Create API Key',
    onPress: onExternalLinkPress(COMMERCE_CREATE_API_LINK),
    image: <CommerceApiKeyImg style={EXAMPLE_IMAGE_STYLE} />,
    darkImage: <CommerceApiKeyImg style={EXAMPLE_IMAGE_STYLE} />,
  },
  {
    title: 'Add a Checkout button',
    description: 'Learn how to use Commerce and the Checkout button start getting paid in USDC.',
    cta: 'View docs',
    onPress: onExternalLinkPress(ONCHAIN_KIT_CHECKOUT_LINK),
    image: <CommerceUseCaseImg style={EXAMPLE_IMAGE_STYLE} />,
    darkImage: <CommerceUseCaseImg style={EXAMPLE_IMAGE_STYLE} />,
  },
  {
    title: 'Use the pre-built Commerce template',
    description: 'Try out the pre-built template to get started selling your products in USDC.',
    cta: 'View template',
    onPress: onExternalLinkPress(COMMERCE_OCK_TEMPLATE_LINK),
    image: <OckTemplateImg style={EXAMPLE_IMAGE_STYLE} />,
    darkImage: <OckTemplateImg style={EXAMPLE_IMAGE_STYLE} />,
  },
];

export function CommerceLanding() {
  const { isDesktop } = useSimpleBreakpoints();

  const headerResponsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '3fr 1fr' : '1fr';
  }, [isDesktop]);

  return (
    <Layout enableMaxWidth={false}>
      <Layout.MainContainer minWidth={MAIN_CONTAINER_MIN_WIDTH} spacingHorizontal={0} fullWidth>
        <VStack>
          <HStack borderedBottom>
            <Grid
              maxWidth={FULL_PAGE_MAX_WIDTH}
              spacing={9}
              gap={4}
              templateColumns={headerResponsiveTemplateColumns}
            >
              {!isDesktop && (
                <HStack>
                  <CommerceHeaderImg style={MOBILE_IMAGE_STYLE} />
                </HStack>
              )}
              <VStack justifyContent="center">
                <TextDisplay3 as="h1">Effortlessly accept USDC payments</TextDisplay3>
                <TextTitle4 as="h2" color="foregroundMuted" spacingTop={1} spacingBottom={7}>
                  Streamline onchain payments with OnchainKit’s easy-to-implement Checkout
                  component, and get real-time merchant tooling including payment tracking,
                  analytics, and reporting.
                </TextTitle4>

                <ButtonGroup>
                  <Button
                    variant="primary"
                    endIcon="diagonalUpArrow"
                    onPress={onExternalLinkPress(ONCHAIN_KIT_CHECKOUT_LINK)}
                    accessibilityLabel="View docs + opens in new window"
                  >
                    View docs
                  </Button>
                  <Button
                    variant="secondary"
                    endIcon="diagonalUpArrow"
                    onPress={onExternalLinkPress(COMMERCE_PORTAL_LINK)}
                    accessibilityLabel="Go to Commerce portal + opens in new window"
                  >
                    Commerce portal
                  </Button>
                </ButtonGroup>
              </VStack>
              {isDesktop && (
                <HStack justifyContent="flex-end" height={262}>
                  <CommerceHeaderImg style={IMAGE_STYLE} />
                </HStack>
              )}
            </Grid>
          </HStack>
          <ExampleSection sectionTitle="Ways to get started" exampleItems={learnItems} />
          <KeyFeaturesSection sectionTitle="Key features" features={keyFeatures} />
        </VStack>
      </Layout.MainContainer>
    </Layout>
  );
}
